import { Fragment, useMemo } from "react";
import { Label } from "@/components/ui/label";
import {
  MultiSelect,
  MultiSelectContent,
  MultiSelectItem,
  MultiSelectTrigger,
  MultiSelectValue,
  useMultiSelectContext,
} from "@/components/ui/library/multi-select";
import {
  buildIntakePairOptions,
  type IntakePairFields,
  type IntakePairOption,
} from "@/features/customField/utils/intakePair";
import { CF_NONE_SENTINEL } from "@/features/customField/utils/cfSentinels";
import { getCfDisplayName } from "@/features/customField/utils/cfDisplayName";
import HighlightText from "../HighlightText";

/**
 * Year-qualified Intake Month picker.
 *
 * Replaces the plain Intake Month multi-select as soon as at least one Intake
 * Year is selected: the options become "January 2026 … December 2027" and the
 * selection is stored as exact `year-month` pairs, so "December 2026 or January
 * 2027" is finally expressible. Two independent selectors could only produce the
 * cross product of both lists.
 */

interface IntakePairFilterProps {
  pair: IntakePairFields;
  /** Option ids of the years currently selected in the Intake Year picker. */
  selectedYearOptionIds: string[];
  /** Selected `"<yearOptionId>-<monthOptionId>"` tokens. */
  value: string[];
  /** Whether the "No <month field>" sentinel is active. */
  noneSelected: boolean;
  /**
   * Receives the raw selection, which may contain the `none` sentinel
   * alongside pair tokens; the caller resolves the two into their stores.
   */
  onChange: (values: string[]) => void;
}

export default function IntakePairFilter({
  pair,
  selectedYearOptionIds,
  value,
  noneSelected,
  onChange,
}: IntakePairFilterProps) {
  const pairOptions = useMemo(
    () => buildIntakePairOptions(pair, selectedYearOptionIds),
    [pair, selectedYearOptionIds]
  );

  // "No <field>" stays available here exactly as it is on every other custom
  // field picker — a year-qualified month list must not quietly remove the only
  // way to find deals whose intake month was never filled in.
  const options = useMemo(
    () => [
      { value: CF_NONE_SENTINEL, label: `No ${getCfDisplayName(pair.monthField)}` },
      ...pairOptions,
    ],
    [pairOptions, pair.monthField]
  );

  const selectedValues = useMemo(
    () => (noneSelected ? [...value, CF_NONE_SENTINEL] : value),
    [value, noneSelected]
  );

  // Year-major groups, in the order `buildIntakePairOptions` already sorted them.
  const groups = useMemo(() => {
    const byYear = new Map<string, IntakePairOption[]>();
    for (const opt of pairOptions) {
      const bucket = byYear.get(opt.yearLabel);
      if (bucket) bucket.push(opt);
      else byYear.set(opt.yearLabel, [opt]);
    }
    return [...byYear.entries()].map(([yearLabel, items]) => ({
      yearLabel,
      items,
    }));
  }, [pairOptions]);

  const fieldName = getCfDisplayName(pair.monthField);
  const lcName = fieldName.toLowerCase();

  return (
    <div className="space-y-2">
      <Label className="text-[14px] leading-[1.35] tracking-[-0.56px] text-[#051912] font-normal mb-0 pb-1.5">
        <HighlightText text={fieldName} />
      </Label>
      <MultiSelect value={selectedValues} onValueChange={onChange}>
        <MultiSelectTrigger className="w-full h-10 bg-[#fcfcfc] border border-[var(--color-black-2)] rounded-[12px] px-3 py-2 text-[14px] leading-[1.35] tracking-[-0.56px] text-[#505e59] font-normal shadow-none hover:bg-[#fcfcfc]">
          <MultiSelectValue
            placeholder={`Select ${lcName}`}
            options={options}
          />
        </MultiSelectTrigger>
        <MultiSelectContent searchPlaceholder={`Search ${lcName}...`}>
          <MultiSelectItem value={CF_NONE_SENTINEL}>
            {`No ${fieldName}`}
          </MultiSelectItem>
          {groups.map((group) => (
            <Fragment key={group.yearLabel}>
              <YearGroupHeader
                yearLabel={group.yearLabel}
                groupValues={group.items.map((o) => o.value)}
              />
              {group.items.map((opt) => (
                <MultiSelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MultiSelectItem>
              ))}
            </Fragment>
          ))}
        </MultiSelectContent>
      </MultiSelect>
      <p className="text-[12px] leading-[1.35] text-[var(--color-text-gray)]">
        Months are listed per selected intake year — pick the exact intakes (e.g.
        December 2026 and January 2027).
      </p>
    </div>
  );
}

/**
 * Sticky-free year heading with an All/Clear toggle for that year's 12 months.
 * Hidden while the user is searching — a heading can't be filtered out by the
 * search the way the items below it are, so it would otherwise float above
 * results belonging to a different year.
 */
function YearGroupHeader({
  yearLabel,
  groupValues,
}: {
  yearLabel: string;
  groupValues: string[];
}) {
  const { value, onValueChange, search } = useMultiSelectContext();
  const allSelected =
    groupValues.length > 0 && groupValues.every((v) => value.includes(v));

  if (search.trim()) return null;

  const toggleYear = () => {
    if (allSelected) {
      onValueChange(value.filter((v) => !groupValues.includes(v)));
    } else {
      onValueChange([...new Set([...value, ...groupValues])]);
    }
  };

  return (
    <div className="flex items-center justify-between gap-2 px-2 pt-2 pb-1">
      <span className="text-[11px] font-medium uppercase tracking-wide text-[var(--color-text-gray)]">
        {yearLabel}
      </span>
      <button
        type="button"
        onClick={toggleYear}
        className="text-[11px] font-medium text-[var(--color-success)] hover:underline cursor-pointer"
      >
        {allSelected ? "Clear" : "All"}
      </button>
    </div>
  );
}
